import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight, CheckCircle2, Shield, Package, Settings, Truck, Megaphone, Award, Target, BarChart3, RefreshCw, Zap } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BlinkitAccountManagement = () => {
  const onboardingServices = [
    'Assistance with Blinkit onboarding',
    'Support in obtaining brand approval',
    'Guidance on required documentation',
    'Coordination with Blinkit category teams when required'
  ];

  const catalogServices = [
    'Creating optimized product listings',
    'Writing keyword-rich product titles and descriptions',
    'Updating product attributes and catalog information',
    'Ensuring catalog accuracy to avoid listing issues'
  ];

  const inventoryServices = [
    'Shipment creation for Blinkit dark stores',
    'Monitoring stock levels to prevent out-of-stock situations',
    'Inventory planning based on demand trends',
    'Ensuring smooth inventory flow to Blinkit dark stores'
  ];

  const advertisingServices = [
    'Setup and management of Blinkit advertising campaigns',
    'Campaign monitoring and optimization',
    'Enrolling products in Blinkit promotions and seasonal campaigns',
    'Improving campaign performance and ROI'
  ];

  const whyChooseUs = [
    { icon: Award, title: 'Complete Blinkit Account Management Support', description: 'End-to-end management of your Blinkit seller account.' },
    { icon: Zap, title: 'Expertise in Quick Commerce Platforms', description: 'Deep understanding of quick commerce dynamics and strategies.' },
    { icon: Package, title: 'Optimized Product Listings & Catalog Management', description: 'Professional catalog optimization for better visibility.' },
    { icon: Truck, title: 'Inventory Planning & Shipment Coordination', description: 'Seamless inventory flow to dark stores.' },
    { icon: Megaphone, title: 'Advertising & Promotion Management', description: 'Strategic campaigns that drive sales and conversions.' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Blinkit Account Management Services | Quick Commerce Experts</title>
        <meta name="description" content="Expert Blinkit account management: inventory, advertising, listing optimization & dark store management. Grow your brand on Blinkit with professionals." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/blinkit-account-management" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/quick-commerce-account-management" className="text-slate-700 hover:text-primary font-medium">Quick Commerce</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Blinkit Account Management</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-28 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 213, 0, 0.05) 0%, rgba(255, 193, 7, 0.05) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-yellow-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center space-x-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap size={16} />
                <span>Quick Commerce Platform</span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Blinkit Account Management & <span className="bg-gradient-to-r from-yellow-500 to-amber-500 bg-clip-text text-transparent">Optimization Services</span>
              </h1>
              <p className="text-lg leading-relaxed text-slate-700 mb-4 max-w-3xl mx-auto">
                Managing a brand on Blinkit requires proper catalog management, inventory planning, advertising strategies, and continuous optimization.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-3xl mx-auto">
                At Obiquiste Solutions, we provide complete Blinkit Account Management & Optimization Services to help brands improve product visibility, maintain inventory flow to dark stores, and increase sales through targeted advertising campaigns.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact?service=blinkit-account">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free Account Audit
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=blinkit-account">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to Expert
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brand Onboarding & Approval Support */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-100 rounded-xl mb-6">
                <Shield className="w-7 h-7 text-yellow-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Brand Onboarding & Approval Support
              </h2>
              <p className="text-lg text-slate-700">
                We assist brands with Blinkit onboarding and brand approval processes. Our team ensures smooth coordination with platform teams to get your brand approved and products listed quickly.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Onboarding Support:</h3>
                <div className="space-y-4">
                  {onboardingServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Listing & Catalog Optimization */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Catalog Services:</h3>
                <div className="space-y-4">
                  {catalogServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-xl mb-6">
                <Package className="w-7 h-7 text-purple-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Product Listing & Catalog Optimization
              </h2>
              <p className="text-lg text-slate-700">
                Optimized product listings help improve discoverability and visibility on Blinkit. We ensure your catalog is accurate, keyword-optimized, and aligned with platform requirements.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Shipment Creation & Inventory Management */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-xl mb-6">
                <Truck className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Shipment Creation & Inventory Management
              </h2>
              <p className="text-lg text-slate-700">
                Inventory planning and shipment coordination are essential for quick commerce platforms like Blinkit. We help ensure your products are always available in dark stores for fast delivery.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Inventory Services:</h3>
                <div className="space-y-4">
                  {inventoryServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advertising & Promotions Management */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Advertising Services:</h3>
                <div className="space-y-4">
                  {advertisingServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-100 rounded-xl mb-6">
                <Megaphone className="w-7 h-7 text-orange-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Advertising & Promotions Management
              </h2>
              <p className="text-lg text-slate-700">
                Advertising and promotions help increase product visibility and sales. We manage your Blinkit ad campaigns and enroll your products in relevant promotions to maximize conversions.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Obiquiste Solutions */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Why Choose Obiquiste Solutions
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              We help brands build a strong presence on Blinkit while improving visibility, conversions, and overall sales performance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-slate-300 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands We Work With */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="w-4 h-4 mr-2" />
              Our Clients
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Brands We Manage on Blinkit
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We are proud to manage Blinkit accounts for these trusted brands, helping them grow on India's leading quick commerce platform.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { name: 'GW Khamkar', category: 'Food Products' },
              { name: 'Vintage Oils', category: 'Edible Oils' },
              { name: 'Fitelo', category: 'Health Foods' },
              { name: 'RajGharana Metals', category: 'Kitchenware' },
              { name: 'HII Health Inside It', category: 'Health & Wellness' },
              { name: 'Auli Lifestyle', category: 'Beauty & Skincare' },
            ].map((brand, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="bg-gradient-to-br from-yellow-50 to-amber-50 p-6 rounded-2xl border border-yellow-200 text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-yellow-600">{brand.name.charAt(0)}</span>
                </div>
                <h3 className="font-heading font-bold text-slate-800 mb-1">{brand.name}</h3>
                <p className="text-xs text-slate-500">{brand.category}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-yellow-50 to-amber-50 rounded-3xl p-12 border border-yellow-200">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Ready to Optimize Your Blinkit Account?
            </h2>
            <p className="text-lg text-slate-700 mb-8">
              Let our experts manage your Blinkit account and help you achieve better visibility, improved inventory flow, and increased sales performance.
            </p>
            <Link to="/contact?service=blinkit-account">
              <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                Get Free Account Audit
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlinkitAccountManagement;
